import type { Company } from './auth/types';

/**
 * Estado de acceso de la empresa (solo lectura / gracia). El backend es la
 * autoridad: /auth/me y /auth/register resuelven `company.access` con NOW() de la
 * DB. Estos helpers son la ÚNICA fuente en el front (no duplicar la lógica en las
 * pantallas). No sustituyen al enforcement del backend (EnforceTrial).
 *
 * Si `access` no vino (tokens/cache viejos), se cae a la lógica LEGACY por fecha
 * (trial vencido o suscripción caída ⇒ solo lectura).
 */

/** Legacy: trial vencido o estado no operativo ⇒ solo lectura (sin campo access). */
function legacyReadOnly(company: Company): boolean {
  if (company.status === 'ACTIVE') return false;
  if (company.status === 'TRIAL') {
    if (!company.trial_ends_at) return false;
    return new Date(company.trial_ends_at).getTime() < Date.now();
  }
  return true; // PAST_DUE / SUSPENDED / CANCELED
}

/**
 * ¿La empresa está en solo-lectura? Bloqueada = sin acceso de escritura. Durante la
 * GRACIA el acceso es total (no es solo-lectura), así que sólo `blocked` bloquea.
 */
export function isReadOnly(company: Company | null): boolean {
  if (!company) return false;
  if (company.access) return company.access === 'blocked';
  return legacyReadOnly(company);
}

/** ¿La empresa está en período de gracia (prueba/pago vencido pero con acceso)? */
export function isInGrace(company: Company | null): boolean {
  return company?.access === 'grace';
}

/** Días de gracia restantes (>=1) o null si no aplica. */
export function graceDaysLeft(company: Company | null): number | null {
  if (!company || company.access !== 'grace') return null;
  return company.grace_days_left ?? null;
}
